import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ScoreRadar from '../../components/charts/ScoreRadar';
import { useMyPageDashboard } from '../../hooks/useMyPageDashboard';
import { CHUGUMI_RESULTS } from '../../data/chugumiTraits';
import { SEB_TEST_DATA } from '../../data/sebTestData';
import { useContent } from '../../content';
import behaviorSurvey from '../../pages/CharacterTest/behavior_survey_v1.json';
import ChugumiMatrix from '../../components/charts/ChugumiMatrix';
import { getResultContentKeys } from '../../utils/loveTestLogic';
import StatusGrid from '../../components/mypage/StatusGrid';
import StatusCard from '../../components/mypage/StatusCard';
import ReportGrid from '../../components/mypage/ReportGrid';
import ReportCard from '../../components/mypage/ReportCard';
import FaceAnalysisCard from '../../components/mypage/FaceAnalysisCard';
import EmptyState from '../../components/mypage/EmptyState';
import { getToken } from '../../auth/auth';

// Copied styles from MyPage.jsx to ensure consistent look
const styles = {
    toggleButton: {
        marginLeft: '1rem',
        padding: '0.4rem 0.8rem',
        fontSize: '0.9rem',
        backgroundColor: 'rgba(96, 165, 250, 0.2)',
        color: '#60a5fa',
        border: '1px solid rgba(96, 165, 250, 0.4)',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    container: {
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        color: 'white',
        padding: '2rem',
        display: 'flex',
        justifyContent: 'center',
    },
    content: {
        width: '100%',
        maxWidth: '1000px',
    },
    title: {
        fontSize: '2rem',
        fontWeight: 'bold',
        marginBottom: '2rem',
        textAlign: 'center',
        background: 'linear-gradient(to right, #60a5fa, #a78bfa)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    headerRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
    },
    backButton: {
        padding: '0.5rem 1rem',
        backgroundColor: '#334155',
        color: 'white',
        textDecoration: 'none',
        borderRadius: '8px',
        fontSize: '0.9rem',
    },
    section: {
        marginBottom: '3rem',
    },
    sectionTitle: {
        fontSize: '1.5rem',
        marginBottom: '1.5rem',
        borderLeft: '4px solid #60a5fa',
        paddingLeft: '1rem',
    },
    resultKey: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#fff',
        marginBottom: '0.5rem',
        wordBreak: 'keep-all',
        lineHeight: '1.4',
    },
    subInfo: {
        color: '#cbd5e1',
        fontSize: '0.9rem',
        marginBottom: '0.5rem',
    },
    date: {
        color: '#64748b',
        fontSize: '0.8rem',
    },
    // Mega Specific
    megaReportContent: { padding: '1rem 0' },
    megaHeader: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '1rem', gap: '0.2rem' },
    megaType: { fontSize: '1.8rem', fontWeight: '900', color: '#60a5fa', letterSpacing: '0.05em' },
    megaName: { fontSize: '1rem', color: '#94a3b8', fontWeight: '600' },

    // Tabs
    tabContainer: {
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
    },
    tabButton: {
        padding: '0.6rem 1.2rem',
        borderRadius: '8px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(255, 255, 255, 0.05)',
        color: '#94a3b8',
        cursor: 'pointer',
        fontSize: '0.95rem',
        transition: 'all 0.2s',
    },
    activeTab: {
        background: 'rgba(59, 130, 246, 0.2)',
        borderColor: '#3b82f6',
        color: 'white',
        fontWeight: '600',
    },
    answerCard: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        marginBottom: '1rem'
    },
    questionMeta: { color: '#94a3b8', fontSize: '0.9rem', marginBottom: '0.5rem' },
    questionText: { fontSize: '1.1rem', fontWeight: 'bold', marginBottom: '1rem' },
    userAnswer: {
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        color: '#60a5fa',
        padding: '1rem',
        borderRadius: '8px',
        fontWeight: '500'
    }
};

const AdminUserDetail = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lang = i18n.language.startsWith('en') ? 'en' : 'ko';
    const { getC, loaded: contentLoaded } = useContent(['love', 'beyond']);

    // Explicit Auth Check
    const [isAuthChecking, setIsAuthChecking] = useState(true);

    useEffect(() => {
        const token = getToken();
        if (!token) {
            alert("로그인이 필요합니다.");
            navigate('/login');
            return;
        }
        setIsAuthChecking(false);
    }, [navigate]);

    // Use the hook with targetUserId to fetch specific user data
    const {
        loading,
        error,
        stats,
        faceResults,
    } = useMyPageDashboard(userId);

    const [showAnswers, setShowAnswers] = useState(false);
    const [activeTab, setActiveTab] = useState('mega'); // 'mega', 'love', 'character', 'beyond'

    if (isAuthChecking) return <div style={{ ...styles.container, color: 'white' }}>인증 확인 중...</div>;
    if (loading) return <div style={{ ...styles.container, color: 'white' }}>데이터 로딩 중...</div>;
    if (error) return <div style={{ ...styles.container, color: '#ef4444' }}>
        <h2>정보를 불러올 수 없습니다.</h2>
        <p>{error}</p>
        <Link to="/startus-admin" style={{ ...styles.backButton, marginTop: '1rem', display: 'inline-block' }}>돌아가기</Link>
    </div>;

    const latestFace = faceResults && faceResults.length > 0 ? faceResults[0] : null;

    // Safely derive answers
    const megaAnswers = stats?.mega?.answers || stats?.mega?.payload?.answers;
    const beyondAnswers = stats?.beyond?.payload?.answers;

    // Love Test Answers (stored in payload or summary depending on legacy/new)
    // Looking at router.py: save_unified_result(..., payload=data.raw) for love test
    // So distinct from Mega which has explicit payload.answers
    const loveAnswers = stats?.love?.payload || stats?.love?.summary?.scores; // Logic might vary, let's inspect structure below

    // Character Test Answers
    const characterAnswers = stats?.character?.payload || stats?.character?.summary?.scores;

    // Helper to check if we have any answers
    const hasAnyAnswers = !!megaAnswers || !!loveAnswers || !!characterAnswers || !!beyondAnswers;

    const handleToggleAnswers = () => {
        if (!hasAnyAnswers) {
            alert("확인 가능한 답안 데이터가 없습니다.");
            return;
        }
        setShowAnswers(!showAnswers);
    };

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <div style={styles.headerRow}>
                    <Link to="/startus-admin" style={styles.backButton}>&larr; 목록으로 돌아가기</Link>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <h1 style={{ ...styles.title, marginBottom: 0 }}>회원 상세 결과: {userId}</h1>
                        <button onClick={handleToggleAnswers} style={styles.toggleButton}>
                            {showAnswers ? '답안 접기' : '답안 보기'}
                        </button>
                    </div>
                    <div style={{ width: 80 }}></div> {/* Spacer for alignment */}
                </div>

                {/* 1. Status Window */}
                <section style={styles.section}>
                    <h2 style={styles.sectionTitle}>종합 상태창</h2>
                    <StatusGrid>
                        <StatusCard title="메가 시너지" empty={!stats?.mega} highlight>
                            {stats?.mega && (
                                <>
                                    <div style={{ ...styles.resultKey, color: '#60a5fa' }}>{stats?.mega?.summary?.mbti}</div>
                                    <div style={styles.subInfo}>{stats?.mega?.result_label}</div>
                                    <div style={styles.date}>{new Date(stats?.mega?.created_at).toLocaleDateString()}</div>
                                </>
                            )}
                        </StatusCard>

                        <StatusCard title="비욘드 테스트" empty={!stats?.beyond} highlight>
                            {stats?.beyond && (
                                <>
                                    <div style={{ ...styles.resultKey, color: '#a78bfa' }}>
                                        {stats.beyond.summary.S}{stats.beyond.summary.E}{stats.beyond.summary.B}
                                    </div>
                                    <div style={styles.subInfo}>이상향 분석</div>
                                    <div style={styles.date}>{new Date(stats?.beyond?.created_at).toLocaleDateString()}</div>
                                </>
                            )}
                        </StatusCard>

                        <StatusCard title="얼굴형 분석" empty={!latestFace}>
                            {latestFace && (
                                <>
                                    <div style={styles.resultKey}>{latestFace.masculinity_label}</div>
                                    <div style={styles.subInfo}>조합: {latestFace.combo_id}</div>
                                    <div style={styles.date}>{new Date(latestFace.timestamp || latestFace.created_at).toLocaleDateString()}</div>
                                </>
                            )}
                        </StatusCard>

                        <StatusCard title="연애 가치관" empty={!stats?.love}>
                            {stats?.love && (() => {
                                const love = stats.love;
                                const payload = love.payload || {};
                                const raw = payload.raw || payload; // Fallback to payload itself for older structure

                                const rrScore = raw.rrScore;
                                const raScore = raw.raScore;
                                const ccFlag = raw.ccFlag;

                                // Re-derive labels if saved ones are faulty or missing
                                let displayLabel = love.result_label;
                                if (!displayLabel || displayLabel.includes('undefined')) {
                                    if (rrScore !== undefined && raScore !== undefined) {
                                        const keys = getResultContentKeys(rrScore, raScore, ccFlag);
                                        const rrL = getC(`love.love_test.types.${keys.rrTypeKey}`);
                                        const raL = getC(`love.love_test.types.${keys.raTypeKey}`);
                                        if (!rrL.includes('missing') && !raL.includes('missing')) {
                                            displayLabel = `${rrL} & ${raL}`;
                                        }
                                    }
                                }

                                return (
                                    <>
                                        <div style={styles.resultKey}>{displayLabel}</div>
                                        <div style={styles.date}>{new Date(love.created_at).toLocaleDateString()}</div>
                                    </>
                                );
                            })()}
                        </StatusCard>

                        <StatusCard title="추구미 분석" empty={!stats?.chugumi}>
                            {stats?.chugumi && (
                                <>
                                    <div style={styles.resultKey}>
                                        {CHUGUMI_RESULTS[stats.chugumi.result_key]?.title || stats.chugumi.result_label || stats.chugumi.result_key}
                                    </div>
                                    <div style={styles.date}>{new Date(stats?.chugumi?.created_at).toLocaleDateString()}</div>
                                </>
                            )}
                        </StatusCard>
                    </StatusGrid>
                </section>

                {/* 2. Analysis Report */}
                <section style={styles.section}>
                    <h2 style={styles.sectionTitle}>심층 분석 리포트</h2>
                    <ReportGrid>
                        {/* Mega Synergy Result */}
                        <ReportCard title="🚀 메가 시너지 분석" highlight>
                            {stats?.mega ? (
                                <div style={styles.megaReportContent}>
                                    <div style={styles.megaHeader}>
                                        <span style={styles.megaType}>{stats.mega.summary.mbti}</span>
                                        <span style={styles.megaName}>{stats.mega.result_label}</span>
                                    </div>
                                    <ScoreRadar
                                        scores={{
                                            max: 100,
                                            axes: [
                                                { key: 'S', label: '안정성', score: stats.mega.summary.score_seb.S },
                                                { key: 'E', label: '공감능력', score: stats.mega.summary.score_seb.E },
                                                { key: 'F', label: '추진력', score: stats.mega.summary.score_seb.F },
                                                { key: 'A', label: '민첩성', score: stats.mega.summary.score_beh.A },
                                                { key: 'B', label: '통찰력', score: stats.mega.summary.score_beh.B },
                                            ]
                                        }}
                                        title="통합 역량 지표"
                                    />
                                </div>
                            ) : <EmptyState text="기록 없음" />}
                        </ReportCard>

                        {/* Beyond Test Result */}
                        <ReportCard title="🪐 비욘드 테스트" highlight>
                            {stats?.beyond ? (
                                <div style={{ textAlign: 'center' }}>
                                    <div style={{ ...styles.resultKey, fontSize: '2rem', letterSpacing: '3px', marginBottom: '1rem', color: '#a78bfa' }}>
                                        {stats.beyond.summary.S}{stats.beyond.summary.E}{stats.beyond.summary.B}
                                    </div>
                                    {stats.beyond.scores && (
                                        <ScoreRadar
                                            scores={{
                                                max: 100,
                                                axes: [
                                                    { key: 'S', label: '정서적 낙원', score: stats.beyond.scores.S },
                                                    { key: 'E', label: '관계의 거리', score: stats.beyond.scores.E },
                                                    { key: 'B', label: '굳건한 신념', score: stats.beyond.scores.B }
                                                ]
                                            }}
                                            title="이상향 지표"
                                        />
                                    )}
                                </div>
                            ) : <EmptyState text="기록 없음" />}
                        </ReportCard>

                        {/* Face Analysis */}
                        <ReportCard title="얼굴형 분석">
                            {latestFace ? (
                                <FaceAnalysisCard faceResult={latestFace} />
                            ) : <EmptyState text="기록 없음" />}
                        </ReportCard>

                        {/* Love Graph */}
                        <ReportCard title="연애 가치관">
                            {stats?.love ? (
                                <ScoreRadar scores={stats?.love?.scores} title="연애 성향 지표" />
                            ) : <EmptyState text="기록 없음" />}
                        </ReportCard>

                        {/* Chugumi Graph */}
                        <ReportCard title="추구미 분석">
                            {stats?.chugumi ? (
                                <div style={{ textAlign: 'center', width: '100%' }}>
                                    <div style={{ ...styles.resultKey, fontSize: '1.4rem', color: '#60a5fa', marginBottom: '1rem' }}>
                                        {CHUGUMI_RESULTS[stats.chugumi.result_key]?.title || stats.chugumi.result_label || stats.chugumi.result_key}
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem', marginBottom: '1rem', color: '#94a3b8', fontSize: '0.9rem' }}>
                                        <span>X축 (Vibe): {stats.chugumi.scores?.vibe || 0}</span>
                                        <span>Y축 (Identity): {stats.chugumi.scores?.identity || 0}</span>
                                    </div>
                                    <ChugumiMatrix
                                        vibeScore={stats.chugumi.scores?.vibe || 24}
                                        identityScore={stats.chugumi.scores?.identity || 24}
                                    />
                                </div>
                            ) : <EmptyState text="기록 없음" />}
                        </ReportCard>
                    </ReportGrid>
                </section>

                {/* 3. Detailed Answers (Admin Only) */}
                {showAnswers && (
                    <section style={styles.section}>
                        <h2 style={styles.sectionTitle}>상세 답안 내역</h2>

                        {/* Tabs */}
                        <div style={styles.tabContainer}>
                            {megaAnswers && (
                                <button
                                    style={{ ...styles.tabButton, ...(activeTab === 'mega' ? styles.activeTab : {}) }}
                                    onClick={() => setActiveTab('mega')}
                                >
                                    메가 시너지
                                </button>
                            )}
                            {beyondAnswers && (
                                <button
                                    style={{ ...styles.tabButton, ...(activeTab === 'beyond' ? styles.activeTab : {}) }}
                                    onClick={() => setActiveTab('beyond')}
                                >
                                    비욘드 테스트
                                </button>
                            )}
                            {stats?.love && (
                                <button
                                    style={{ ...styles.tabButton, ...(activeTab === 'love' ? styles.activeTab : {}) }}
                                    onClick={() => setActiveTab('love')}
                                >
                                    연애 가치관
                                </button>
                            )}
                            {stats?.character && (
                                <button
                                    style={{ ...styles.tabButton, ...(activeTab === 'character' ? styles.activeTab : {}) }}
                                    onClick={() => setActiveTab('character')}
                                >
                                    성향 분석
                                </button>
                            )}
                        </div>

                        {/* Content */}
                        <div style={{ display: 'grid', gap: '1rem' }}>

                            {/* MEGA TEST ANSWERS */}
                            {activeTab === 'mega' && megaAnswers && SEB_TEST_DATA.questions.map((q) => {
                                const answerIdx = megaAnswers[q.id];
                                if (answerIdx === undefined) return null;
                                return (
                                    <div key={q.id} style={styles.answerCard}>
                                        <div style={styles.questionMeta}>Q{q.id}. {q.category} ({q.axis})</div>
                                        <div style={styles.questionText}>{q.q[lang] || q.q.ko}</div>
                                        <div style={styles.userAnswer}>
                                            선택한 답안: {q.a[lang] !== undefined ? q.a[lang][answerIdx] : (q.a.ko[answerIdx] || <span style={{ color: 'red' }}>데이터 오류</span>)}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* BEYOND TEST ANSWERS */}
                            {activeTab === 'beyond' && beyondAnswers && contentLoaded && Array.from({ length: 15 }, (_, i) => i + 1).map((qId) => {
                                const qText = getC(`beyond.beyond_test.questions.${qId}.q`);
                                const userChoice = beyondAnswers[qId]; // "A", "B", "C", "D"
                                const answerText = getC(`beyond.beyond_test.questions.${qId}.a.${userChoice}`);

                                if (!qText || qText.includes('missing')) return null;

                                return (
                                    <div key={qId} style={styles.answerCard}>
                                        <div style={styles.questionMeta}>Q{qId}. Beyond Test (Ideal)</div>
                                        <div style={styles.questionText}>{qText}</div>
                                        <div style={styles.userAnswer}>
                                            {userChoice ? (
                                                <>
                                                    <span style={{ fontWeight: 'bold', marginRight: '8px', color: '#a78bfa' }}>[{userChoice}]</span>
                                                    {answerText}
                                                </>
                                            ) : (
                                                <span style={{ color: '#64748b' }}>미응답</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* LOVE TEST ANSWERS */}
                            {activeTab === 'love' && stats?.love && contentLoaded && Array.from({ length: 15 }, (_, i) => i + 1).map((qId) => {
                                const q = getC(`love.love_test.questions.${qId}`);
                                if (!q) return null;

                                const rawAnswers = stats.love.payload || {};
                                const userChoice = rawAnswers[qId] || rawAnswers[String(qId)];

                                return (
                                    <div key={qId} style={styles.answerCard}>
                                        <div style={styles.questionMeta}>Q{qId}. Love Test</div>
                                        <div style={styles.questionText}>{q.q}</div>
                                        <div style={styles.userAnswer}>
                                            {userChoice ? (
                                                <>
                                                    <span style={{ fontWeight: 'bold', marginRight: '8px' }}>[{userChoice}]</span>
                                                    {q.a?.[userChoice] || '알 수 없는 답안'}
                                                </>
                                            ) : (
                                                <span style={{ color: '#64748b' }}>미응답</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}

                            {/* CHARACTER TEST ANSWERS */}
                            {activeTab === 'character' && stats?.character && behaviorSurvey.questions.map((q) => {
                                // Character answers are stored as: { "1": { optionId: 2, effects: ... } }
                                const rawAnswers = stats.character.payload || {};
                                // Q1 -> extract 1
                                const qNum = q.question_id.replace('Q', '');
                                const userEntry = rawAnswers[qNum] || rawAnswers[String(qNum)];

                                const userOptionId = userEntry?.optionId;
                                const selectedOption = q.options.find(o => o.option_id === userOptionId);

                                return (
                                    <div key={q.question_id} style={styles.answerCard}>
                                        <div style={styles.questionMeta}>{q.question_id}. {q.section}</div>
                                        <div style={styles.questionText}>{q.text}</div>
                                        <div style={styles.userAnswer}>
                                            {selectedOption ? (
                                                selectedOption.text
                                            ) : (
                                                <span style={{ color: '#64748b' }}>미응답 (ID: {userOptionId})</span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}
            </div>
        </div >
    );
};

export default AdminUserDetail;
